import { merge as _merge } from 'lodash';

import { localeMap } from 'utils/currencyFormat';
import { sanitizeProductData } from 'analytics/helpers';
import track from 'analytics/track';

export const viewedProductConfig = {
  enhancedEcommerce: {
    actionData: `ec:addProduct`,
    actionSet: `ec:setAction`,
    type: `detail`,
  },
  params: {
    ga: {
      eventAction: `Viewed Product`,
      eventLabel: `Product PDP`,
      nonInteraction: true,
    },
    gtm: {
      brand: `Casper`,
      event: `Viewed Product`,
    },
    heap: {
      eventName: `Viewed Product`,
      properties: {
        placement: `Product PDP`,
      },
    },
  },
};

export const galleryNavigationConfig = {
  params: {
    ga: {
      eventAction: `Buy Module Image Gallery`,
      nonInteraction: false,
    },
    heap: {
      eventName: `Buy Module Image Gallery`,
    },
  },
};

export const badgeTrialConfig = {
  params: {
    ga: {
      eventCategory: `Casper`,
      eventAction: `Expand Trial Message`,
      eventLabel: `100 Night Trial`,
    },
    heap: {
      eventName: `Expand Trial Message`,
      properties: {
        category:  `Casper`,
      },
    },
  },
};

export const specNavigationConfig = {
  params: {
    ga: {
      eventCategory: `Spec Title`,
      eventAction: `Expand Product Specifications`,
      eventLabel: `Product PDP`,
    },
    heap: {
      eventName: `Expand Product Specifications`,
      properties: {
        name: `Spec Title`,
        placement: `Product PDP`,
      },
    },
  },
};

export const productImpressionConfig = {
  enhancedEcommerce: {
    actionData: `ec:addImpression`,
  },
  params: {
    ga: {
      eventAction: `Product Thumbnail Impression`,
      nonInteraction: true,
    },
    heap: {
      eventName: `Product Thumbnail Impression`,
    },
  },
};

export const productClickConfig = {
  enhancedEcommerce: {
    actionData: `ec:addProduct`,
    actionSet: `ec:setAction`,
    type: `click`,
  },
  params: {
    ga: {
      eventAction: `Product Thumbnail Click`,
      nonInteraction: true,
    },
    heap: {
      eventName: `Product Thumbnail Click`,
    },
  },
};

/**
 * Calls the viewed product event from the respective product's PDP
 * @param {object} productData The product data needed by the track call
 * @param {string} locale The locale that this viewed product event is being called in
*/
export const viewedProductPDP = (productData, locale) => {
  const sanitizedProductData = {
    ...sanitizeProductData(productData.variantData),
    category: productData.category,
    quantity: productData.quantity,
  };

  const currency = localeMap[locale].code;

  const trackParams = _merge({}, viewedProductConfig, {
    enhancedEcommerce: {
      data: sanitizedProductData,
    },
    params: {
      ga: {
        eventCategory: productData.category,
      },
      gtm: {
        category: productData.category,
        currency,
        name: sanitizedProductData.name,
        sku: sanitizedProductData.id,
      },
      heap: {
        properties: {
          category: productData.category,
        },
      },
    },
  });

  track(trackParams);
};

/**
 * Calls the gallery navigation event from the PDP's main gallery in the purchase module
 * @param {object} product the product data needed by the track call
 * @param {number} index the index of the next slide
*/
export const galleryNavigation = (product, index) => {
  const trackParams = _merge({}, galleryNavigationConfig, {
    params: {
      ga: {
        eventCategory: product,
        eventLabel: index.toString(),
      },
      heap: {
        properties: {
          placement: product,
          galleryIndex: index,
        },
      },
    },
  });
  track(trackParams);
};

/**
 * Calls the gallery navigation event from the PDP's main gallery in the purchase module
 * @param {object} product the product data needed by the track call
*/
export const badgeTrialExpand = (product) => {
  const trackParams = _merge({}, badgeTrialConfig, {
    params: {
      ga: {
        eventCategory: product,
      },
      heap: {
        properties: {
          category:  product,
        },
      },
    },
  });
  track(trackParams);
};

/**
 * Calls the gallery navigation event from the PDP's main gallery in the purchase module
 * @param {string} specPlacement the page the specs are on (eg. "Casper PDP")
 * @param {string} specLabel the spec category (eg. "Warranty", or "Measurements and Dimensions")
*/
export const specNavigation = (specPlacement, specLabel) => {
  const trackParams = _merge({}, specNavigationConfig, {
    params: {
      ga: {
        eventCategory: specLabel,
        eventLabel: specPlacement,
      },
      heap: {
        properties: {
          name: specLabel,
          placement: specPlacement,
        },
      },
    },
  });
  track(trackParams);
};

/**
 * Calls the product impression event from the cross sell module
 * @param {string} product the product data needed by the track call
 * @param {number} position the position of the item in its respective list
*/
export const productThumbnailImpressionCrossSell = (product, position) => {
  const impressionFieldObject = {
    name: product,
    list: `PDP Cross-sell`,
    position: position + 1,
    brand: `Casper`,
    category: product,
  };

  const trackParams = _merge({}, productImpressionConfig, {
    enhancedEcommerce: {
      data: impressionFieldObject,
    },
    params: {
      ga: {
        eventCategory: product,
        eventLabel: `PDP Cross-sell`,
      },
      heap: {
        properties: {
          placement: `PDP Cross-sell`,
        },
      },
    },
  });
  track(trackParams);
};

/**
 * Calls the product click event from the cross sell module
 * @param {object} product the product data needed by the track call
 * @param {number} position the position of the item in its respective list
*/
export const productThumbnailClickCrossSell = (product, position) => {
  const productFieldObject = {
    name: product,
    position: position + 1,
    brand: `Casper`,
    category: product,
  };

  const trackParams = _merge({}, productClickConfig, {
    enhancedEcommerce: {
      data: productFieldObject,
    },
    params: {
      ga: {
        eventCategory: product,
        eventLabel: `PDP Cross-sell`,
      },
      heap: {
        properties: {
          placement: `PDP Cross-sell`,
        },
      },
    },
  });
  track(trackParams);
};

/**
 * Calls the select size event from the respective product's PDP
 * @param {string} productCategory the respective product's product category
 * @param {string} size the size that is being selected
*/
export const selectSize = (productCategory, size) => {
  const trackParams = {
    params: {
      ga: {
        eventAction: `Select Size`,
        eventCategory: productCategory,
        eventLabel: size,
        nonInteraction: false,
      },
      heap: {
        eventName: `Select Size`,
        properties: {
          size,
        },
      },
    },
  };
  track(trackParams);
};

/**
 * Calls the product thumbnail impression event
 * @param {string} productCategory the respective product's product category
 * @param {string} productName the size that is being selected
 * @param {number} position the position of the item in its respective list
*/
export const productThumbnailImpressionSubnav = (productCategory, productName, position) => {
  const trackParams = _merge({}, productImpressionConfig, {
    enhancedEcommerce: {
      data: {
        name: productName,
        list: `Drawer`,
        position: position + 1,
        brand: `Casper`,
        category: productCategory,
      },
    },
    params: {
      ga: {
        eventCategory: productCategory,
        eventLabel: `Drawer`,
      },
      heap: {
        properties: {
          placement: `Drawer`,
        },
      },
    },
  });
  track(trackParams);
};

/**
 * Calls the product thumbnail click event from the sub nav
 * @param {object} params an object that holds the product name and product category
 * @param {number} position the position of the item in its respective list
*/
export const productThumbnailClickSubnav = (params, position) => {
  const productName = params.name;
  const productCategory = params.category;

  const trackParams = _merge({}, productClickConfig, {
    enhancedEcommerce: {
      data: {
        name: productName,
        position: position + 1,
        brand: `Casper`,
        category: productCategory,
      },
    },
    params: {
      ga: {
        eventCategory: productCategory,
        eventLabel: `Drawer`,
      },
      heap: {
        properties: {
          placement: `Drawer`,
        },
      },
    },
  });
  track(trackParams);
};

/**
 * Calls the product thumbnail impression event from PDP
 * @param {string} productCategory The respective product's product category
 * @param {string} productName The size that is being selected
 * @param {number} index The position of the item in its respective list
*/
export const productThumbnailImpressionPDP = (productCategory, productName, index) => {
  const position = `pdp_${index}`;
  const trackParams = _merge({}, productImpressionConfig, {
    enhancedEcommerce: {
      data: {
        name: productName,
        list: `PDP Cross-sell`,
        position,
        brand: `Casper`,
        category: productCategory,
      },
    },
    params: {
      ga: {
        eventCategory: productCategory,
        eventLabel: `PDP Cross-sell`,
      },
      heap: {
        properties: {
          placement: `PDP Cross-sell`,
        },
      },
    },
  });
  track(trackParams);
};

/**
 * Calls the product thumbnail click event from PDP
 * @param {string} productCategory The respective product's product category
 * @param {string} productName The size that is being selected
 * @param {number} index The position of the product thumbnail
*/
export const productThumbnailClickPDP = (productCategory, productName, index) => {
  const position = `pdp_${index}`;
  const trackParams = _merge({}, productClickConfig, {
    enhancedEcommerce: {
      data: {
        name: productName,
        position,
        brand: `Casper`,
        category: productCategory,
      },
    },
    params: {
      ga: {
        eventCategory: productCategory,
        eventLabel: `PDP Cross-sell`,
      },
      heap: {
        properties: {
          placement: `PDP Cross-sell`,
        },
      },
    },
  });
  track(trackParams);
};

/**
 * Calls the product thumbnail impression event
 * @param {string} productCategory The respective product's product category
 * @param {string} productName The size that is being selected
 * @param {number} index The position of the item in its respective list
*/
export const productThumbnailImpressionHomepage = (productCategory, productName, index) => {
  const position = `pip_${index}`;
  const trackParams = _merge({}, productImpressionConfig, {
    enhancedEcommerce: {
      data: {
        name: productName,
        list: `Homepage`,
        position,
        brand: `Casper`,
        category: productCategory,
      },
    },
    params: {
      ga: {
        eventCategory: productCategory,
        eventLabel: `Homepage`,
      },
      heap: {
        properties: {
          placement: `Homepage`,
        },
      },
    },
  });
  track(trackParams);
};

/**
 * Calls the product thumbnail click event from the homepage
 * @param {string} productCategory The respective product's product category
 * @param {string} productName The size that is being selected
 * @param {number} index The position of the product thumbnail
*/
export const productThumbnailClickHomepage = (productCategory, productName, index) => {
  const position = `pip_${index}`;
  const trackParams = _merge({}, productClickConfig, {
    enhancedEcommerce: {
      data: {
        name: productName,
        position,
        brand: `Casper`,
        category: productCategory,
      },
    },
    params: {
      ga: {
        eventCategory: productCategory,
        eventLabel: `Homepage`,
      },
      heap: {
        properties: {
          placement: `Homepage`,
        },
      },
    },
  });
  track(trackParams);
};

/**
 * Calls the product thumbnail impression event
 * @param {string} productCategory The respective product's product category
 * @param {string} productName The products name
*/
export const productThumbnailImpressionPIP = (productCategory, productName) => {
  const trackParams = _merge({}, productImpressionConfig, {
    enhancedEcommerce: {
      data: {
        name: productName,
        list: `Product Index Page`,
        brand: `Casper`,
        category: productCategory,
      },
    },
    params: {
      ga: {
        eventCategory: productCategory,
        eventLabel: `Product Index Page`,
      },
      heap: {
        properties: {
          placement: `Product Index Page`,
        },
      },
    },
  });
  track(trackParams);
};

/**
 * Calls the product thumbnail click event from the homepage
 * @param {string} productCategory The respective product's product category
 * @param {string} productName The products name
*/
export const productThumbnailClickPIP = (productCategory, productName) => {
  const trackParams = _merge({}, productClickConfig, {
    enhancedEcommerce: {
      data: {
        name: productName,
        brand: `Casper`,
        category: productCategory,
      },
    },
    params: {
      ga: {
        eventCategory: productCategory,
        eventLabel: `Product Index Page`,
      },
      heap: {
        properties: {
          placement: `Product Index Page`,
        },
      },
    },
  });
  track(trackParams);
};

/**
 * Calls the product thumbnail impression event from the Mattress Comparison Page
 * @param {string} productCategory The respective product's product category
 * @param {string} productName The products name
*/
export const productThumbnailImpressionPCP = (productCategory, productName) => {
  const trackParams = _merge({}, productImpressionConfig, {
    enhancedEcommerce: {
      data: {
        name: productName,
        list: `Product Category Page`,
        brand: `Casper`,
        category: productCategory,
      },
    },
    params: {
      ga: {
        eventCategory: productCategory,
        eventLabel: `Product Category Page`,
      },
      heap: {
        properties: {
          placement: `Product Category Page`,
        },
      },
    },
  });
  track(trackParams);
};

/**
 * Calls the product thumbnail click event from the Mattress Comparison Page
 * @param {string} productCategory The respective product's product category
 * @param {string} productName The products name
*/
export const productThumbnailClickPCP = (productCategory, productName) => {
  const trackParams = _merge({}, productClickConfig, {
    enhancedEcommerce: {
      data: {
        name: productName,
        brand: `Casper`,
        category: productCategory,
      },
    },
    params: {
      ga: {
        eventCategory: productCategory,
        eventLabel: `Product Category Page`,
      },
      heap: {
        properties: {
          placement: `Product Category Page`,
        },
      },
    },
  });
  track(trackParams);
};

/**
 * Sends video event data to heap to to match that which is sent automatically to GA
 * @param {string} videoName The name of the wistia video
 * @param {string} videoPlacement Where on the site the video is
 * @param {string} percentageWatched The percentage of the video that's been watched
 */
const trackWistiaVideoEvent = (videoName, videoPlacement, percentageWatched) => {
  const percentageWatchedText = percentageWatched === 0 ? `Play` : `${percentageWatched}% Watched`;
  const trackParams = {
    params: {
      ga: {
        eventAction: `Video ${percentageWatchedText}`,
        eventCategory: `Video`,
        eventLabel: `${videoName}.${videoPlacement}`,
      },
      heap: {
        eventName: `Video ${percentageWatchedText}`,
        properties: {
          video_name: videoName, // eslint-disable-line
          video_placement: videoPlacement, // eslint-disable-line
        },
      },
    },
  };
  track(trackParams);
};

export const setupVideoTrackingEvents = (wistiaVideoObject, videoPlacement) => {
  if (!videoPlacement) {
    throw new Error(`Missing parameter`);
  }

  const videoName = wistiaVideoObject.data.media.name;

  wistiaVideoObject.bind(`percentwatchedchanged`, function(percent, lastPercent) {
    if (percent >= .01 && lastPercent < .01) {
      trackWistiaVideoEvent(videoName, videoPlacement, 0);
    } else if (percent >= .25 && lastPercent < .25) {
      trackWistiaVideoEvent(videoName, videoPlacement, 25);
    } else if (percent >= .5 && lastPercent < .5) {
      trackWistiaVideoEvent(videoName, videoPlacement, 50);
    } else if (percent >= .75 && lastPercent < .75) {
      trackWistiaVideoEvent(videoName, videoPlacement, 75);
    } else if (percent >= 1 && lastPercent < 1) {
      trackWistiaVideoEvent(videoName, videoPlacement, 100);
    }
  });
};
